import * as React from "react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "./SearchableSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { resolveFieldError } from "./formFieldError";

/**
 * SelectField - Custom select component following nxplan pattern
 * @param {Object} props
 * @param {Array} props.options - Array of {key|value, label|value, hidden?, disabled?}
 * @param {string} props.value - Selected key
 * @param {function} props.onChange - Callback with synthetic event {target: {value, id}}
 * @param {function} props.onValueChange - Callback with raw value
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.label - Label text
 * @param {string} props.error - Error message
 * @param {string} props.emptyLabel - Disabled empty-state option label
 * @param {boolean} props.required - Show required asterisk
 * @param {boolean} props.disabled - Disable the select
 * @param {string} props.id - Element ID
 * @param {string} props.className - Container className
 * @param {string} props.selectClassName - Select button className
 * @param {string} props.contentClassName - Select options panel className
 * @param {string} props.labelClassName - Label className
 * @param {boolean} props.searchable - Force searchable select
 * @param {number} props.searchableThreshold - Use searchable select when options exceed threshold
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {string} props.emptyText - Empty state text for searchable select
 * @param {boolean} props.sortOptions - Sort normalized options before rendering
 * @param {boolean} props.disablePortal - Render dropdown content without a portal
 * @param {boolean} props.allowClear - Allow a searchable selection to be cleared
 * @param {boolean} props.lockSingleOption - Auto-select and lock a single searchable option
 * @param {string} props.ariaLabel - Accessible name of the control (no visible label)
 * @param {string} props.ariaLabelledBy - Id of the element naming the control
 */
export const SelectField = ({
  options = [],
  onChange,
  onValueChange,
  value,
  placeholder,
  label,
  error,
  emptyLabel,
  required = false,
  disabled = false,
  id,
  className,
  selectClassName,
  contentClassName,
  labelClassName,
  searchable = false,
  searchableThreshold = 8,
  searchPlaceholder,
  emptyText,
  sortOptions = true,
  disablePortal = false,
  allowClear = false,
  lockSingleOption = true,
  ariaLabel,
  ariaLabelledBy,
}) => {
  const { t } = useTran();
  const resolvedPlaceholder = placeholder ?? t("common.select_an_option");
  const normalizedOptions = (options || [])
    .map((option) => {
      if (option == null) return null;
      if (typeof option === "string" || typeof option === "number") {
        const text = String(option);
        return { key: text, label: text, disabled: false, hidden: false };
      }
      const key = option.key ?? option.value ?? option.id ?? "";
      if (key === null || key === undefined || key === "") return null;
      return {
        key: String(key),
        label:
          option.label ??
          option.value ??
          option.key ??
          option.id ??
          String(key),
        disabled: Boolean(option.disabled),
        hidden: Boolean(option.hidden),
      };
    })
    .filter(Boolean)
    .filter((option) => !option.hidden);
  const orderedOptions = sortOptions
    ? normalizedOptions.sort((a, b) => {
        const aKey = String(a.key).trim().toLowerCase();
        const bKey = String(b.key).trim().toLowerCase();
        const aLabel = String(a.label).trim().toLowerCase();
        const bLabel = String(b.label).trim().toLowerCase();
        const aIsAll = aKey === "all" || aLabel === "all";
        const bIsAll = bKey === "all" || bLabel === "all";
        if (aIsAll && !bIsAll) return -1;
        if (!aIsAll && bIsAll) return 1;

        const aNum = Number(a.label);
        const bNum = Number(b.label);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }

        return String(a.label).localeCompare(String(b.label));
      })
    : normalizedOptions;
  const uniqueOptions = [];
  const seenOptionKeys = new Set();
  orderedOptions.forEach((option) => {
    if (seenOptionKeys.has(option.key)) return;
    seenOptionKeys.add(option.key);
    uniqueOptions.push(option);
  });

  const displayOptions =
    uniqueOptions.length === 0 && emptyLabel
      ? [
          {
            key: "__empty",
            label: emptyLabel,
            disabled: true,
            hidden: false,
          },
        ]
      : uniqueOptions;

  const shouldSearch = searchable || uniqueOptions.length > searchableThreshold;

  const isEmpty =
    value === "" || value === null || typeof value === "undefined";
  const normalizedValueForSelect = isEmpty ? undefined : String(value);

  const handleValueChange = (selectedValue) => {
    onValueChange?.(selectedValue);
    if (!onChange) return;
    const syntheticEvent = {
      target: {
        value: selectedValue,
        id,
      },
    };
    onChange(syntheticEvent);
  };

  return (
    <div className={cn("space-y-2", className)} id={id}>
      {label && (
        <Label className={labelClassName}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      {shouldSearch ? (
        <SearchableSelect
          options={uniqueOptions.map((option) => ({
            value: option.key,
            label: option.label,
            disabled: option.disabled,
          }))}
          value={
            value === "" || value === null || typeof value === "undefined"
              ? ""
              : String(value)
          }
          onChange={handleValueChange}
          placeholder={resolvedPlaceholder}
          emptyText={emptyText || emptyLabel}
          searchPlaceholder={searchPlaceholder}
          disabled={disabled}
          disablePortal={disablePortal}
          allowClear={allowClear}
          lockSingleOption={lockSingleOption}
          ariaLabel={ariaLabel}
          ariaLabelledBy={ariaLabelledBy}
          contentClassName={contentClassName}
          className={cn(
            error && "border-destructive dark:border-destructive",
            selectClassName,
          )}
        />
      ) : (
        // Radix Select stays uncontrolled when value is undefined; switching
        // back to undefined after a real selection doesn't clear the visual.
        // Toggle the key between "empty" and "set" to force a remount only
        // on that transition, not on every value change.
        <Select
          key={isEmpty ? "empty" : "set"}
          value={normalizedValueForSelect}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          <SelectTrigger
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            className={cn(
              "w-full",
              error && "border-destructive dark:border-destructive",
              selectClassName,
            )}
          >
            <div className="truncate w-full text-left">
              <SelectValue placeholder={resolvedPlaceholder} />
            </div>
          </SelectTrigger>
          <SelectContent
            position="popper"
            disablePortal={disablePortal}
            className={cn("max-w-[calc(100vw-1rem)]", contentClassName)}
          >
            {displayOptions.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                disabled={option.disabled}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {error && (
        <small className="text-destructive text-xs">
          {resolveFieldError(t, error)}
        </small>
      )}
    </div>
  );
};
